import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api from "../lib/apiClient";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Check, Loader2, Sparkles, CreditCard, Tag, XCircle, CheckCircle2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";

const FEATURES = [
  "Unlimited digital referrals", "Unlimited referral history", "PDF generation & QR codes",
  "Secure referral links", "WhatsApp & email sharing", "Saved templates & duplication",
  "Digital signature & clinic branding", "Automatic referral IDs",
];

export default function Subscription() {
  const { subscription, refreshSubscription } = useAuth();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [promo, setPromo] = useState("");
  const [polling, setPolling] = useState(false);

  useEffect(() => {
    const sid = params.get("session_id");
    if (sid) { setPolling(true); pollStatus(sid, 0); }
  }, []);

  const pollStatus = async (sid, attempt) => {
    if (attempt >= 6) { setPolling(false); toast.error("Payment status timed out. Refresh if you completed payment."); return; }
    try {
      const { data } = await api.get(`/subscription/status/${sid}`);
      if (data.payment_status === "paid") {
        setPolling(false);
        await refreshSubscription();
        toast.success("Subscription activated! 🎉");
        setParams({});
        return;
      }
      if (data.status === "expired") { setPolling(false); toast.error("Payment session expired."); return; }
      setTimeout(() => pollStatus(sid, attempt + 1), 2000);
    } catch (e) { setTimeout(() => pollStatus(sid, attempt + 1), 2000); }
  };

  const checkout = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/subscription/checkout", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) { toast.error("Could not start checkout"); setLoading(false); }
  };

  const applyPromo = async () => {
    if (!promo.trim()) return;
    try { await api.post("/subscription/promo", { code: promo }); await refreshSubscription(); toast.success("Promo applied — subscription active!"); setPromo(""); }
    catch (e) { toast.error(e.response?.data?.detail || "Invalid promo code"); }
  };

  const cancel = async () => {
    try { await api.post("/subscription/cancel"); await refreshSubscription(); toast.success("Subscription cancelled"); }
    catch (e) { toast.error("Could not cancel"); }
  };

  const status = subscription?.status;
  const isActive = subscription?.can_create;
  const statusLabel = { trial: "Free Trial", active: "Active", complimentary: "Complimentary", trial_expired: "Trial Expired", expired: "Expired", cancelled: "Cancelled" }[status] || status;

  return (
    <div className="p-6 md:p-8 max-w-4xl mx-auto">
      <h1 className="text-3xl font-bold tracking-tight text-slate-900 font-head">Subscription</h1>
      <p className="text-slate-500 mt-1">Manage your ReferU Professional plan.</p>

      {polling && (
        <div className="mt-6 bg-blue-50 border border-blue-100 rounded-2xl p-4 flex items-center gap-3">
          <Loader2 className="w-5 h-5 animate-spin text-blue-600" /><p className="text-blue-800 text-sm font-medium">Confirming your payment…</p>
        </div>
      )}

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 mt-6 flex items-center justify-between flex-wrap gap-4" data-testid="current-plan-card">
        <div>
          <p className="text-sm text-slate-500">Current status</p>
          <div className="flex items-center gap-2 mt-1">
            {isActive ? <CheckCircle2 className="w-5 h-5 text-emerald-500" /> : <XCircle className="w-5 h-5 text-amber-500" />}
            <span className="text-xl font-semibold text-slate-900 font-head">{statusLabel}</span>
          </div>
          {subscription?.trial_ends_at && status === "trial" && <p className="text-xs text-slate-400 mt-1">Trial ends {subscription.trial_ends_at.slice(0, 10)}</p>}
          {subscription?.subscription_ends_at && status === "active" && !subscription?.cancel_at_period_end && <p className="text-xs text-slate-400 mt-1">Auto-renews {subscription.subscription_ends_at.slice(0, 10)} · $5.99/mo</p>}
          {subscription?.cancel_at_period_end && subscription?.subscription_ends_at && <p className="text-xs text-amber-600 mt-1">Cancels on {subscription.subscription_ends_at.slice(0, 10)} — access until then</p>}
        </div>
        {status === "active" && !subscription?.cancel_at_period_end && <Button data-testid="cancel-sub-btn" variant="outline" onClick={cancel} className="rounded-xl border-slate-300">Cancel subscription</Button>}
      </div>

      <div className="grid md:grid-cols-2 gap-6 mt-6">
        <div className="bg-white rounded-2xl border-2 border-blue-200 shadow-md p-7">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-blue-600" />
            <p className="text-xs font-semibold tracking-[0.2em] uppercase text-blue-600">ReferU Professional</p>
          </div>
          <div className="flex items-end gap-1 mt-3">
            <span className="text-5xl font-bold text-slate-900 font-head">$5.99</span><span className="text-slate-500 mb-2">/month</span>
          </div>
          <ul className="mt-5 space-y-2">
            {FEATURES.map((f) => <li key={f} className="flex items-center gap-2 text-sm text-slate-700"><Check className="w-4 h-4 text-emerald-500 shrink-0" /> {f}</li>)}
          </ul>
          <Button data-testid="subscribe-btn" onClick={checkout} disabled={loading} className="w-full mt-6 rounded-xl h-12 bg-blue-600 hover:bg-blue-700">
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <><CreditCard className="w-4 h-4 mr-1.5" /> {isActive && status === "active" ? "Renew now" : "Subscribe with Stripe"}</>}
          </Button>
        </div>

        <div className="space-y-6">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
            <h3 className="font-semibold text-slate-900 font-head flex items-center gap-2"><Tag className="w-5 h-5 text-blue-600" /> Have a promo code?</h3>
            <div className="flex gap-2 mt-4">
              <Input data-testid="promo-input" value={promo} onChange={(e) => setPromo(e.target.value)} placeholder="WELCOME30" className="h-12 rounded-xl" />
              <Button data-testid="apply-promo-btn" onClick={applyPromo} variant="outline" className="rounded-xl h-12 border-slate-300">Apply</Button>
            </div>
          </div>
          <div className="bg-slate-50 rounded-2xl border border-slate-200 p-6">
            <h3 className="font-semibold text-slate-900 font-head">Billing notes</h3>
            <p className="text-sm text-slate-500 mt-2">This is an auto-renewing monthly subscription ($5.99/mo) billed securely by Stripe. You can cancel anytime — you'll keep access until the end of your billing period. After your trial, an active subscription is required to create or send new referrals.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
